import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";

/**
 * Print-optimised one-pager for an IdeaLab submission.
 * Renders into a clean A4 layout, hides all chrome, and auto-fires window.print()
 * once the data is loaded.
 *
 * Open via `/admin/idealab/:ref/brief` (typically target="_blank" from the detail page).
 */
export default function IdeaLabAdminBrief() {
  const { ref } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/idealab/admin/submissions/${ref}`)
      .then(({ data }) => { if (!cancelled) setSub(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ref]);

  useEffect(() => {
    if (!loading && sub) {
      // Slight delay so the layout has painted before the dialog appears
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [loading, sub]);

  if (loading) return <div className="p-10 text-zinc-500 font-mono">Loading…</div>;
  if (!sub) return <div className="p-10">Not found.</div>;

  const c = sub.contact || {};
  const a = sub.answers || {};
  const ai = sub.ai_summary || {};
  const la = sub.lead_assessment || {};

  return (
    <div className="brief-root" data-testid="admin-idealab-brief">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; }
        }
        .brief-root {
          max-width: 190mm;
          margin: 12mm auto;
          padding: 10mm 12mm;
          background: #fff;
          color: #0a0a0a;
          font-family: 'Manrope', system-ui, sans-serif;
          font-size: 9.5pt;
          line-height: 1.4;
        }
        .brief-root h1 { font-size: 18pt; font-weight: 800; margin: 0 0 2pt; letter-spacing: -0.02em; }
        .brief-root h2 { font-size: 9pt; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #FF4D2E; margin: 10pt 0 4pt; border-bottom: 0.5pt solid #e4e4e7; padding-bottom: 2pt; }
        .brief-root .meta { font-family: 'JetBrains Mono', monospace; font-size: 8pt; letter-spacing: 1.5px; text-transform: uppercase; color: #71717a; }
        .brief-root dl { display: grid; grid-template-columns: 32mm 1fr; gap: 1pt 6pt; margin: 0; }
        .brief-root dt { font-family: 'JetBrains Mono', monospace; font-size: 7pt; letter-spacing: 1.2px; text-transform: uppercase; color: #71717a; align-self: start; padding-top: 2pt; }
        .brief-root dd { margin: 0 0 2pt 0; }
        .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 4mm 10mm; }
        .pill { display: inline-block; padding: 2pt 6pt; background: #0a0a0a; color: #fff; font-family: 'JetBrains Mono', monospace; font-size: 7pt; letter-spacing: 1.2px; text-transform: uppercase; }
        .pill-high   { background: #047857; }
        .pill-medium { background: #d97706; }
        .pill-low    { background: #525252; }
        .actions { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
        .actions button { background: #0a0a0a; color: #fff; border: none; padding: 6px 12px; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; cursor: pointer; }
        .actions button.ghost { background: #fff; color: #0a0a0a; border: 1px solid #e4e4e7; }
        .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4pt 8pt; }
        .stat .label { font-family: 'JetBrains Mono', monospace; font-size: 7pt; letter-spacing: 1.2px; text-transform: uppercase; color: #71717a; margin-bottom: 1pt; }
        .stat .value { font-size: 11pt; font-weight: 700; }
        p { margin: 0 0 4pt; }
        ul { margin: 0 0 4pt; padding-left: 16pt; }
        li { margin: 0 0 1pt; }
        .narrow { max-width: 95%; }
      `}</style>

      {/* On-screen controls — hidden on print */}
      <div className="actions no-print">
        <button onClick={() => window.print()}>Print again</button>
        <button className="ghost" onClick={() => window.close()}>Close</button>
      </div>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "1pt solid #FF4D2E", paddingBottom: "4pt", marginBottom: "6pt" }}>
        <div>
          <div className="meta">Appistry IdeaLab · Discovery Brief</div>
          <h1>{c.business_name || c.organisation || c.full_name || "—"}</h1>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="meta">{sub.reference_number}</div>
          <div className="meta">{fmtDate(sub.submitted_at)}</div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="summary-grid" style={{ marginBottom: "6pt" }}>
        <div className="stat"><div className="label">Readiness</div><div className="value"><Pill v={la.lead_readiness} /></div></div>
        <div className="stat"><div className="label">Complexity</div><div className="value"><Pill v={la.complexity} /></div></div>
        <div className="stat"><div className="label">Investment band</div><div className="value">{a.budget_band || "—"}</div></div>
        <div className="stat"><div className="label">Target launch</div><div className="value">{a.target_launch_date || "—"}</div></div>
      </div>

      <h2>Contact</h2>
      <dl>
        <dt>Contact</dt><dd>{c.full_name || "—"}</dd>
        <dt>Business</dt><dd>{c.business_name || c.organisation || "—"}</dd>
        <dt>Email</dt><dd>{c.email || "—"}</dd>
        <dt>Telephone</dt><dd>{c.telephone || "—"}</dd>
        <dt>Website</dt><dd>{c.website || "—"}</dd>
        <dt>Preferred</dt><dd>{c.preferred_contact || "—"}</dd>
      </dl>

      {Object.keys(ai).length > 0 && (
        <>
          <h2>AI synopsis</h2>
          {ai.business_challenge && <p><strong>Challenge.</strong> {ai.business_challenge}</p>}
          {ai.proposed_solution && <p><strong>Solution.</strong> {ai.proposed_solution}</p>}
          {ai.tech_approach && <p><strong>Approach.</strong> {ai.tech_approach}</p>}
          {ai.next_step && <p><strong>Next step.</strong> {ai.next_step}{ai.next_step_rationale ? ` — ${ai.next_step_rationale}` : ""}</p>}
        </>
      )}

      <h2>The idea</h2>
      <div className="two-col">
        <div>
          {a.business_description && <p><strong>Business.</strong> {a.business_description}</p>}
          {a.idea_description && <p><strong>Idea.</strong> {a.idea_description}</p>}
          {a.inspiration && <p><strong>Inspiration.</strong> {a.inspiration}</p>}
        </div>
        <div>
          {a.success_vision && <p><strong>Success (12m).</strong> {a.success_vision}</p>}
          {a.top_three_v1 && <p><strong>V1 top three.</strong> {String(a.top_three_v1).replace(/\n/g, " · ")}</p>}
        </div>
      </div>

      <h2>Customers</h2>
      <div className="two-col">
        <div>
          {a.customer_who && <p><strong>Who.</strong> {a.customer_who}</p>}
          {a.customer_challenges && <p><strong>Challenges.</strong> {a.customer_challenges}</p>}
          {a.customer_outcomes && <p><strong>Outcomes.</strong> {a.customer_outcomes}</p>}
        </div>
        <div>
          <p><strong>Location.</strong> {a.customer_location || "—"}</p>
          <p><strong>Existing audience.</strong> {(a.customer_audience || []).join(" · ") || "—"}</p>
        </div>
      </div>

      <h2>Offering & access</h2>
      <div className="two-col">
        <div>
          <p><strong>Offerings.</strong> {(a.offerings || []).join(" · ") || "—"}</p>
          <p><strong>Existing content.</strong> {a.has_existing_content || "—"} · {a.content_amount || "—"} · continues: {a.will_continue_creating || "—"}</p>
        </div>
        <div>
          <p><strong>Access model.</strong> {a.access_model || "—"}</p>
          <p><strong>Memberships.</strong> {a.membership_levels || "—"} · accounts: {a.user_accounts || "—"}</p>
          <p><strong>Sign-in.</strong> {(a.signin_methods || []).join(" · ") || "—"}</p>
        </div>
      </div>

      <h2>AI & existing setup</h2>
      <div className="two-col">
        <div>
          <p><strong>AI features.</strong> {(a.ai_features || []).join(" · ") || "—"}</p>
          <p><strong>Content for AI.</strong> {a.ai_content_support || "—"}</p>
        </div>
        <div>
          <p><strong>Existing.</strong> {(a.existing_setup || []).join(" · ") || "—"}</p>
          {a.domain_name && <p><strong>Domain.</strong> {a.domain_name} · managed by {a.domain_manager || "?"} · access: {a.domain_access || "?"}</p>}
          {a.accepts_payments_online && <p><strong>Payments.</strong> {a.accepts_payments_online}{a.payment_platform ? ` via ${a.payment_platform}` : ""}</p>}
        </div>
      </div>

      <h2>Brand & style</h2>
      <div className="two-col">
        <div>
          {a.brand_three_words && <p><strong>Three words.</strong> {a.brand_three_words}</p>}
          <p><strong>Feelings.</strong> {(a.brand_feelings || []).join(" · ") || "—"}</p>
          <p><strong>Assets.</strong> {(a.brand_assets || []).join(" · ") || "—"}</p>
        </div>
        <div>
          {a.brand_colors_like && <p><strong>Colours like.</strong> {a.brand_colors_like}</p>}
          {a.brand_colors_dislike && <p><strong>Avoid.</strong> {a.brand_colors_dislike}</p>}
          {a.brand_inspirations && <p className="narrow"><strong>Inspirations.</strong> {a.brand_inspirations}</p>}
        </div>
      </div>

      <h2>Budget & next steps</h2>
      <p>{a.budget_statement || "—"}</p>
      {a.anything_else && <p><strong>Other notes.</strong> {a.anything_else}</p>}

      {(sub.ai_turns || []).length > 0 && (
        <>
          <h2>Idea Partner Q&amp;A</h2>
          {sub.ai_turns.map((t, i) => (
            <p key={i}><strong>Q.</strong> {t.question}{t.answer ? <> &nbsp;<strong>A.</strong> {t.answer}</> : ""}</p>
          ))}
        </>
      )}

      <div style={{ marginTop: "10pt", borderTop: "0.5pt solid #e4e4e7", paddingTop: "6pt", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10pt" }}>
        <div className="meta" style={{ flex: 1 }}>
          © Appistry — Confidential discovery brief — Reference {sub.reference_number}
          <div style={{ marginTop: "3pt", textTransform: "none", letterSpacing: 0, fontSize: "7.5pt" }}>
            Scan to open the live record in admin (and type up your handwritten notes against the original).
          </div>
        </div>
        <div style={{ flexShrink: 0, textAlign: "center" }}>
          <QRCodeSVG
            value={`${window.location.origin}/admin/idealab/${sub.reference_number}`}
            size={62}
            level="M"
            includeMargin={false}
          />
          <div className="meta" style={{ marginTop: "2pt", fontSize: "6pt" }}>{sub.reference_number}</div>
        </div>
      </div>
    </div>
  );
}

function Pill({ v }) {
  if (!v) return <span style={{ color: "#a1a1aa" }}>—</span>;
  const cls = v === "High" ? "pill-high" : v === "Medium" ? "pill-medium" : "pill-low";
  return <span className={`pill ${cls}`}>{v}</span>;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
